import React, { useRef, useState } from 'react'

import LabelAndTextInput from '@components/inputs/LabelAndTextInput'
import Title from '@components/title/Title'
import Pictogram from '@components/pictogram/Pictogram'
import cash from '@assets/form/euro.svg'
import loan from '@assets/form/loan.svg'
import {
	setContributionAmount,
	setInterestRate,
	setLoanAmount,
	setLoanDuration,
} from './fundingSlice'
import { useAppDispatch, useAppSelector } from '@/hooks'
import type { RootState } from '@/store'
import { requiredValidator, numberValidator, decimalValidator, schemaValidator } from '@/utils/validators'
import Validator from '@/pages/form/validator/Validator'
import type { FieldRules } from '../fieldRules'

import './Section.css'

interface FundingProps {
	fundingType: string
	onSetFundingType: (value: string) => void
	// What the schema says about each field.
	rules: FieldRules
	// The answers the whole form shares, for the schema's conditions.
	answers: Record<string, string>
	fundingTypeOptions?: string[]
}

function Funding(props: FundingProps) {
	const { rules } = props
	const answers = { ...props.answers, fundingType: props.fundingType }
	const fundingTypeOptions = props.fundingTypeOptions ?? []
	// Whether the schema shows any of these questions for the answers so far.
	const hasField = (...keys: string[]) => keys.some(key => rules.shows(key, answers))
	const canChooseLoan = fundingTypeOptions.length === 0 || fundingTypeOptions.includes('loan')
	const canChooseCash = fundingTypeOptions.length === 0 || fundingTypeOptions.includes('cash')
	// The pictograms are not inputs: the choice is mirrored in a hidden one so
	// the validator can require it.
	const fundingTypeRef = useRef<HTMLInputElement>(null)
	const [fundingTypeErrors, setFundingTypeErrors] = useState<string[]>([])

	const chooseFundingType = (value: string) => {
		setFundingTypeErrors([])
		props.onSetFundingType(value)
	}

	if (!hasField('fundingType', 'contributionAmount', 'loanAmount', 'loanDuration', 'interestRate')) {
		return null
	}

	return (
		<div className='section'>
			<Title>Votre financement</Title>
			{hasField('fundingType') && (
				<Validator
					id='fundingType'
					validator={(input: string) =>
						rules.requires('fundingType') ? [requiredValidator(input, 'Choisissez un mode de financement.')] : []
					}
					reference={fundingTypeRef}
					onError={setFundingTypeErrors}
				>
					<input id='fundingType' ref={fundingTypeRef} type='hidden' value={props.fundingType} readOnly />
					<div className='pictograms'>
						{canChooseLoan && (
							<Pictogram
								className={'picto1' + (props.fundingType === 'loan' ? ' active' : '')}
								src={loan}
								id='loan'
								onChange={() => chooseFundingType('loan')}
							>
								Crédit immobilier
							</Pictogram>
						)}
						{canChooseCash && (
							<Pictogram
								className={'picto1' + (props.fundingType === 'cash' ? ' active' : '')}
								src={cash}
								id='cash'
								onChange={() => chooseFundingType('cash')}
							>
								Comptant
							</Pictogram>
						)}
					</div>
					{fundingTypeErrors.map((error, index) =>
						error !== '' ? (
							<span key={index} className='error text-center'>
								{error}
							</span>
						) : null,
					)}
				</Validator>
			)}
			{hasField('contributionAmount', 'loanAmount', 'loanDuration', 'interestRate') && (
				<Loan rules={rules} answers={answers} />
			)}
		</div>
	)
}

function Loan(props: { rules: FieldRules; answers: Record<string, string> }) {
	const dispatch = useAppDispatch()
	const { rules } = props
	const hasField = (...keys: string[]) => keys.some(key => rules.shows(key, props.answers))

	const getContributionAmount = useAppSelector(
		(state: RootState) => state.funding.contributionAmount,
	)
	const onSetContributionAmount = (value: string) => {
		dispatch(setContributionAmount(value))
	}

	const getLoanAmount = useAppSelector((state: RootState) => state.funding.loanAmount)
	const onSetLoanAmount = (value: string) => {
		dispatch(setLoanAmount(value))
	}

	const getLoanDuration = useAppSelector((state: RootState) => state.funding.loanDuration)
	const onSetLoanDuration = (value: string) => {
		dispatch(setLoanDuration(value))
	}

	const getInterestRate = useAppSelector((state: RootState) => state.funding.interestRate)
	const onSetInterestRate = (value: string) => {
		dispatch(setInterestRate(value))
	}

	return (
		<div>
			<div className='formContent'>
				{hasField('contributionAmount', 'loanAmount') && (
					<div className='fieldLine'>
						{hasField('contributionAmount') && (
							<LabelAndTextInput
								id='contributionAmount'
								label="Montant de l'apport"
								name='contributionAmount'
								value={getContributionAmount}
								placeholder='Ex : 50 000 €'
								onChange={onSetContributionAmount}
								validator={schemaValidator(rules.requires('contributionAmount'), numberValidator)}
								autocomplete='number'
							/>
						)}
						{hasField('loanAmount') && (
							<LabelAndTextInput
								id='loanAmount'
								label='Montant du prêt'
								name='loanAmount'
								placeholder='Ex : 450 000 €'
								value={getLoanAmount}
								onChange={onSetLoanAmount}
								validator={schemaValidator(rules.requires('loanAmount'), numberValidator)}
								autocomplete='number'
							/>
						)}
					</div>
				)}
				{hasField('loanDuration', 'interestRate') && (
					<div className='fieldLine'>
						{hasField('loanDuration') && (
							<LabelAndTextInput
								id='loanDuration'
								label='Durée du prêt'
								name='loanDuration'
								placeholder='Ex : 20 ans'
								value={getLoanDuration}
								onChange={onSetLoanDuration}
								validator={schemaValidator(rules.requires('loanDuration'), numberValidator)}
								autocomplete='number'
							/>
						)}
						{hasField('interestRate') && (
							<LabelAndTextInput
								id='interestRate'
								type='decimal'
								label='Taux d’intérêt'
								name='interestRate'
								placeholder='Ex : 3,8 %'
								value={getInterestRate}
								onChange={onSetInterestRate}
								validator={schemaValidator(rules.requires('interestRate'), decimalValidator)}
								autocomplete='number'
							/>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

export default Funding
